// Translates leftover hardcoded Arabic UI text in the DOM when the UI language is en/ur (cached server + localStorage).
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AR = /[\u0621-\u064A]/;
const UR = /[ٹڈڑںےۓہھ]/;
const ISO = /[\u2066-\u2069]/g;
const NUM = /[\d٠-٩][\d٠-٩,.٬٫]*/g;
const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "CODE", "PRE", "NOSCRIPT"]);

let lang = "ar";
let cache = {};
let over = {};
let org = "";
const nodes = new Map(); // text node -> {src, out}
const attrs = new Map(); // element -> {attr: {src, out}}
const queued = new Set();
const inflight = new Set();
let timer = null;
let observer = null;

const loadCache = () => { try { cache = JSON.parse(localStorage.getItem(`wz_tr_${lang}`) || "{}"); } catch { cache = {}; } };
const saveCache = () => {
  const keys = Object.keys(cache);
  if (keys.length > 6000) keys.slice(0, keys.length - 6000).forEach((k) => delete cache[k]);
  try { localStorage.setItem(`wz_tr_${lang}`, JSON.stringify(cache)); } catch { /* quota */ }
};

const needs = (v) => v && v.length <= 400 && AR.test(v) && !(lang === "ur" && UR.test(v));
const split = (v) => {
  const clean = v.replace(ISO, "");
  const m = clean.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const nums = [];
  const key = m[2].replace(NUM, (x) => `{${nums.push(x) - 1}}`);
  return { lead: m[1], key, nums, trail: m[3] };
};
const render = (src) => {
  const { lead, key, nums, trail } = split(src);
  const tr = over[key] ?? cache[key];
  if (tr == null) { if (!inflight.has(key)) { queued.add(key); schedule(); } return null; }
  const fix = (x) => (lang === "en" ? x.replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)).replace(/٬/g, ",").replace(/٫/g, ".") : x);
  return lead + tr.replace(/\{(\d+)\}/g, (x, i) => (nums[i] != null ? fix(nums[i]) : x)) + trail;
};

const skipEl = (el) => !el || SKIP.has(el.tagName) || el.isContentEditable || el.closest?.("[data-no-translate]");

const doText = (n) => {
  const v = n.nodeValue;
  const rec = nodes.get(n);
  if (rec && v === rec.out) return;
  if (!needs(v) || skipEl(n.parentElement)) { nodes.delete(n); return; }
  const r = { src: v, out: null };
  nodes.set(n, r);
  const out = render(v);
  if (out != null && out !== v) { r.out = out; n.nodeValue = out; ltr(n.parentElement); }
};
const ltr = (el) => { if (lang === "en" && el && el.closest("[dir=rtl]") && !el.hasAttribute("dir")) el.setAttribute("dir", "auto"); };
const doAttr = (el, a) => {
  const v = el.getAttribute(a);
  const m = attrs.get(el) || {};
  if (m[a] && v === m[a].out) return;
  if (!needs(v) || skipEl(el)) { delete m[a]; return; }
  m[a] = { src: v, out: null };
  attrs.set(el, m);
  const out = render(v);
  if (out != null) { m[a].out = out; el.setAttribute(a, out); }
};
const walk = (root) => {
  if (root.nodeType === 3) return doText(root);
  if (root.nodeType !== 1 || SKIP.has(root.tagName)) return;
  ATTRS.forEach((a) => root.hasAttribute(a) && doAttr(root, a));
  root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")).forEach((el) => ATTRS.forEach((a) => el.hasAttribute(a) && doAttr(el, a)));
  const it = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = it.nextNode(); n; n = it.nextNode()) doText(n);
};

const reapply = () => {
  nodes.forEach((r, n) => {
    if (!n.isConnected) { nodes.delete(n); return; }
    if (r.out == null) { const out = render(r.src); if (out != null) { r.out = out; n.nodeValue = out; ltr(n.parentElement); } }
  });
  attrs.forEach((m, el) => {
    if (!el.isConnected) { attrs.delete(el); return; }
    Object.entries(m).forEach(([a, r]) => { if (r.out == null) { const out = render(r.src); if (out != null) { r.out = out; el.setAttribute(a, out); } } });
  });
};

function schedule() {
  if (timer) return;
  timer = setTimeout(flush, 150);
}
async function flush() {
  timer = null;
  const want = lang;
  const keys = [...queued].slice(0, 100);
  keys.forEach((k) => { queued.delete(k); inflight.add(k); });
  if (queued.size) schedule();
  if (!keys.length) return;
  try {
    const { data } = await axios.post(`${API}/i18n/auto`, { lang: want, texts: keys });
    if (want !== lang) return;
    Object.assign(cache, data.map || {});
    saveCache();
    reapply();
  } catch { /* keep Arabic */ } finally { keys.forEach((k) => inflight.delete(k)); }
}

const restore = () => {
  nodes.forEach((r, n) => { if (n.isConnected && r.out != null && n.nodeValue === r.out) n.nodeValue = r.src; });
  attrs.forEach((m, el) => Object.entries(m).forEach(([a, r]) => { if (r.out != null && el.getAttribute(a) === r.out) el.setAttribute(a, r.src); }));
  nodes.clear(); attrs.clear(); queued.clear();
};

const loadOverrides = async () => {
  const want = `${org}:${lang}`;
  if (!org || lang === "ar") { over = {}; return; }
  try {
    const { data } = await axios.get(`${API}/i18n/overrides/${org}`, { params: { lang } });
    if (want !== `${org}:${lang}`) return;
    over = data.map || {};
    nodes.forEach((r, n) => { if (n.isConnected && r.out != null && n.nodeValue === r.out) { const o = render(r.src); if (o != null) { r.out = o; n.nodeValue = o; } } });
    attrs.forEach((m, el) => Object.entries(m).forEach(([a, r]) => { if (r.out != null && el.getAttribute(a) === r.out) { const o = render(r.src); if (o != null) { r.out = o; el.setAttribute(a, o); } } }));
    reapply();
  } catch { /* ignore */ }
};

// Company-specific wording set by the manager; call again after edits to refresh.
export const setAutoOrg = (orgId) => { org = orgId || ""; loadOverrides(); };

export const setAutoLang = (l) => {
  if (l === lang || typeof MutationObserver === "undefined" || !document.body) return;
  restore();
  lang = l;
  if (lang === "ar") { observer?.disconnect(); observer = null; return; }
  loadCache();
  over = {};
  loadOverrides();
  walk(document.body);
  if (!observer) {
    observer = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") doText(m.target);
        else if (m.type === "attributes") doAttr(m.target, m.attributeName);
        else m.addedNodes.forEach(walk);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  }
};
